import {
    PG_CART_SESSION_UUID,
    WL_CART_SESSION_UUID,
} from '@/config/cookieNames'
import {
    FEATURE_FLAGS,
    MEDIUM_TYPE_CARD_TO_CARD,
    MEDIUM_TYPE_PHYSICAL,
} from '@/prisma/schemaConstants'
import useCheckUserKYCApplicationStatus from '@/src/common/hooks/useCheckUserKYCApplicationStatus'
import buildGoogleEcommerceItemsFromCart from '@/src/common/utilities/analytics/buildGoogleEcommerceItemsFromCart'
import cardLimitValidator from '@/src/common/utilities/cardLimitValidator'
import { enteredCheckout } from '@/src/common/utilities/gtag'
import { removeLineItemFromCart } from '@/src/common/utilities/removeLineItemFromCart'
import { useUser } from '@/src/modules/auth/context/AuthProvider'
import { ICart, IDelivery } from '@/src/modules/cart/ICart'
import CheckoutBaseFormat from '@/src/modules/checkout/CheckoutBaseFormat'
import { CheckoutPaymentProvider } from '@/src/modules/checkout/CheckoutPaymentContext'
import ShippingStep from '@/src/modules/checkout/ShippingStep/ShippingStep'
import KYCApplicationDeniedContent from '@/src/modules/checkout/content/KYCApplicationDeniedContent'
import CheckoutForm from '@/src/modules/checkout/forms/CheckoutForm'
import KYCApplicationForm from '@/src/modules/checkout/forms/KYCApplicationForm'
import { removeBoxItemFromCart } from '@/src/modules/checkout/utilities/removeBoxItemFromCart'
import { TkYCStatus } from '@/src/modules/kyc/types'
import flatMap from 'lodash/flatMap'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { useCookies } from 'react-cookie'
import { ClipLoader } from 'react-spinners'
import LinkButton from '@/src/common/components/marketing/buttons/LinkButton'
import useGetCartManually from '@/src/common/hooks/useGetCartManually'
import usePostAddCart from '@/src/common/hooks/usePostAddCart'
import { LoadingOverlay } from '@/src/modules/checkout/forms/StyledComponents'
import { useCartContext } from '@/src/common/contexts/CartContext'
import { getAmazonReturnUrl } from '@/src/common/utilities/determineOrderingDomain'
import { PayPalScriptProvider } from '@paypal/react-paypal-js'
import prisma from '@/lib/prisma'
import { CartEmptyContainer } from '@/src/modules/checkout/StyledComponents'
import { TCheckoutProps } from '@/src/modules/checkout/utilities/types'
import determineCheckoutUrls from '@/src/modules/checkout/utilities/determineCheckoutUrls'
import getEmailDeliveries from '@/src/modules/checkout/utilities/getEmailDeliveries'

const fetchCart = ({
    refetchCart,
    setCartData,
    setRequiresKyc,
}: {
    refetchCart: any
    setCartData: Function
    setRequiresKyc: Function
}) => {
    refetchCart().then((response: any) => {
        setCartData(response?.data)
        setRequiresKyc(response?.data?.resolved_data?.requires_kyc_application)
    })
}

export default function CheckoutPage({
    amazonPayURL,
    paypalClientId,
    isPayPalEnabled,
    partnerData,
}: Readonly<TCheckoutProps>) {
    const [currentStep, setCurrentStep] = useState(1)
    const [hasPlastic, setHasPlastic] = useState(false)
    const [showPageLoader, setShowPageLoader] = useState<boolean>(false)
    const [checkoutStep1AnalyticSent, setCheckoutStep1AnalyticSent] =
        useState<boolean>(false)
    const [shippingDetailsSatisfied, setShippingDetailsSatisfied] =
        useState<boolean>(false)
    const [forceShippingStep, setForceShippingStep] = useState(false)

    const { mutate: updateCart } = usePostAddCart()
    const {
        refetch: refetchCart,
        isLoading: refetchIsLoading,
        isSuccess: fetchIsSuccess,
    } = useGetCartManually()
    const { refetch: refetchKYC } = useCheckUserKYCApplicationStatus()
    const [cookies] = useCookies([PG_CART_SESSION_UUID, WL_CART_SESSION_UUID])

    const [checkedKycStatus, setCheckedKycStatus] = useState(false)
    const [hasKycApplication, setHasKycApplication] = useState<boolean>(false)
    const [kycIsRejected, setKycIsRejected] = useState<boolean>(false)

    const [cartData, setCartData] = useState<ICart>()
    const cartSessionId =
        cookies[partnerData ? WL_CART_SESSION_UUID : PG_CART_SESSION_UUID]

    const [promoCode, setPromoCode] = useState<string | null>(null)
    const {
        setCartTotalLoad,
        setCartTotalCards,
        overLimitRequiresKYC,
        showKYCForm,
    } = useCartContext()

    const [requiresKyc, setRequiresKyc] = useState<boolean>(false)

    const isCartEmpty = useMemo(() => {
        return (
            (cartData && cartData?.resolved_data?.items_count <= 0) ||
            !cartSessionId
        )
    }, [cartData, cartSessionId])

    const [paymentProcessor, setPaymentProcessor] = useState('')

    const emailDeliveries: IDelivery[] = getEmailDeliveries(cartData)
    const virtualLineItemEmails: string[] = flatMap(
        emailDeliveries,
        (delivery: IDelivery) => {
            return delivery.delivery_details.recipient_email_address ?? ''
        }
    )

    const { total, numberOfCards } = cardLimitValidator({
        cartData: cartData,
        isEditing: false,
        quantityToEditFromCart: 0,
    })

    // forces user to login at checkout if KYC application required
    const router = useRouter()
    const { isLoggedIn, isLoaded } = useUser()
    const requiresLoginForKYC: boolean = isLoaded && !isLoggedIn && requiresKyc

    // get urls for partner or not
    const { backUrlBase, checkoutUrl, continueShoppingUrl, loginUrl, homeUrl } =
        determineCheckoutUrls(partnerData)

    useEffect(() => {
        const urlParams = new URLSearchParams(window?.location?.search)

        const processor = urlParams.get('fiserv') ? 'fiserv' : 'stripe'
        setPaymentProcessor(processor)

        const onRouteChange = () => {
            const urlParams = new URLSearchParams(window?.location?.search)
            const shippingStep = urlParams.get('step') === 'shipping'
            setForceShippingStep(shippingStep)
        }

        router.events.on('routeChangeComplete', onRouteChange)

        return () => {
            router.events.off('routeChangeComplete', onRouteChange)
        }
    }, [])

    useEffect(() => {
        if (requiresLoginForKYC) {
            router.replace(`${loginUrl}?type=checkout&kyc=1`).then(() => {})
        }
    }, [loginUrl, requiresLoginForKYC, router])

    useEffect(() => {
        if (cartData?.cart_contents?.deliveries && !checkoutStep1AnalyticSent) {
            const formattedCartData =
                buildGoogleEcommerceItemsFromCart(cartData)
            enteredCheckout(formattedCartData)
            setCheckoutStep1AnalyticSent(true)
        }
    }, [cartData, checkoutStep1AnalyticSent])

    useEffect(() => {
        if (
            ((overLimitRequiresKYC || requiresKyc) &&
                (!checkedKycStatus || !hasKycApplication)) ||
            showKYCForm
        ) {
            refetchKYC().then((response: any) => {
                const data = response?.data?.data
                setKycIsRejected(data?.status === TkYCStatus.REJECTED)
                setHasKycApplication(!!data?.id)
                setCheckedKycStatus(true)
            })
        }
    }, [
        checkedKycStatus,
        hasKycApplication,
        refetchKYC,
        requiresKyc,
        showKYCForm,
        overLimitRequiresKYC,
    ])

    useEffect(() => {
        fetchCart({
            refetchCart,
            setCartData,
            setRequiresKyc,
        })
    }, [refetchCart])

    useEffect(() => {
        const cartDeliveries = cartData?.cart_contents?.deliveries ?? []
        const allPhysicalItems = cartDeliveries.filter((delivery) =>
            delivery.line_items.every(
                (lineItem) =>
                    lineItem.product?.resolved_data?.product_medium
                        ?.medium_type === MEDIUM_TYPE_PHYSICAL
            )
        )
        const hasValidShippingDetails = !!allPhysicalItems?.every(
            (delivery) => {
                const address = delivery.delivery_details?.address
                const deliveryMethodUuid =
                    delivery.delivery_option?.delivery_option_uuid
                if (!address || !deliveryMethodUuid) {
                    return false
                }
                const { name, address1, city, state, zip } = address
                return (
                    name &&
                    address1 &&
                    city &&
                    state &&
                    zip &&
                    deliveryMethodUuid
                )
            }
        )
        setHasPlastic(!!allPhysicalItems?.length)
        setShippingDetailsSatisfied(hasValidShippingDetails)
    }, [cartData])

    useEffect(() => {
        const plasticStep: number = shippingDetailsSatisfied ? 2 : 1
        hasPlastic ? setCurrentStep(plasticStep) : setCurrentStep(1)
    }, [hasPlastic, shippingDetailsSatisfied])

    function setCartContextValues(cartData: ICart) {
        setCartTotalCards(cartData.resolved_data.items_count)
        setCartTotalLoad(cartData.resolved_data.load_value_total)
    }

    function removeItemFromCart({
        lineItemUuid,
        boxUuid,
    }: {
        lineItemUuid?: string
        boxUuid?: string
    }) {
        setShowPageLoader(true)
        let newCartData = null

        if (lineItemUuid) {
            newCartData = removeLineItemFromCart(
                cartData as ICart,
                lineItemUuid
            )
        }
        if (boxUuid) {
            newCartData = removeBoxItemFromCart(cartData as ICart, boxUuid)
        }
        if (promoCode) {
            newCartData = { ...newCartData, promo_code: promoCode }
        }

        updateCart(
            {
                ...newCartData,
                isPartnerFlow: !!partnerData,
                hasPartnerCart: !!cartSessionId,
            },
            {
                onSuccess: (data) => {
                    setCartData(data as ICart)
                    setCartContextValues(data as ICart)
                    setShowPageLoader(false)
                },
                onError: (error: any) => {
                    console.error('Issue updating cart:', error)
                    setShowPageLoader(false)
                },
            }
        )
    }

    const updatePromoCode = (code: string) => {
        setShowPageLoader(true)
        setPromoCode(code)
        const newCartData = {
            ...cartData,
            promo_code: code,
            isPartnerFlow: !!partnerData,
            hasPartnerCart: !!cartSessionId,
        }
        updateCart(newCartData, {
            onSuccess: (data) => {
                setCartData(data as ICart)
                setShowPageLoader(false)
            },
            onError: () => {
                alert('Failed to update cart.')
                setShowPageLoader(false)
            },
        })
    }

    const addDeliveriesToCart = (newDeliveries: IDelivery[]) => {
        const nonShippingDeliveries = (
            cartData?.cart_contents.deliveries ?? []
        ).filter((delivery) => {
            return delivery.line_items.every((lineItem) => {
                return (
                    lineItem.product?.resolved_data?.product_medium
                        .medium_type !== MEDIUM_TYPE_PHYSICAL
                )
            })
        })

        const updatedCart = {
            ...cartData,
            cart_contents: {
                ...(cartData?.cart_contents ?? {}),
                deliveries: [...nonShippingDeliveries, ...newDeliveries],
            },
            resolved_data: undefined,
            promo_code: promoCode,
            isPartnerFlow: !!partnerData,
            hasPartnerCart: !!cartSessionId,
        }

        updateCart(updatedCart, {
            onSuccess: (data) => {
                setCartData(data as ICart)
                router.push(checkoutUrl).then()
            },
            onError: (error) => {
                console.error(error)
                alert('Failed to update cart.')
            },
        })
    }

    const hasPGPlusItems: boolean = !!cartData?.cart_contents?.deliveries?.find(
        (delivery) =>
            delivery?.line_items?.find(
                (item) =>
                    item?.product?.resolved_data?.product_medium
                        ?.medium_type === MEDIUM_TYPE_CARD_TO_CARD
            )
    )

    if (fetchIsSuccess && isCartEmpty) {
        return (
            <CheckoutBaseFormat
                currentStep={currentStep}
                hasPlastic={hasPlastic}
                cartSummary={{
                    cartData: cartData,
                    removeItemFromCart: removeItemFromCart,
                    updatePromoCode: updatePromoCode,
                    showPromoCodeInput: false,
                    partnerData: partnerData,
                }}
            >
                <CartEmptyContainer>
                    <h2>Your cart is empty</h2>
                    <p>Looks like you haven&apos;t added anything to your cart yet.</p>
                    <LinkButton
                        variant={'primary'}
                        size={'lg'}
                        buttonWidth={''}
                        textTransform={'uppercase'}
                        label={'Continue Shopping'}
                        url={continueShoppingUrl}
                    />
                </CartEmptyContainer>
            </CheckoutBaseFormat>
        )
    }

    if (
        !cartData ||
        refetchIsLoading ||
        requiresLoginForKYC ||
        !isLoaded ||
        (requiresKyc && !checkedKycStatus)
    ) {
        return (
            <CheckoutBaseFormat
                currentStep={currentStep}
                hasPlastic={hasPlastic}
                cartSummary={{
                    cartData: cartData,
                    removeItemFromCart: removeItemFromCart,
                    updatePromoCode: updatePromoCode,
                    showPromoCodeInput: false,
                    partnerData: partnerData,
                }}
            >
                <LoadingOverlay>
                    <ClipLoader color={'black'} loading={true} size={30} />
                </LoadingOverlay>
            </CheckoutBaseFormat>
        )
    }

    if ((showKYCForm || requiresKyc) && !hasKycApplication && isLoggedIn) {
        return (
            <CheckoutBaseFormat
                currentStep={currentStep}
                hasPlastic={hasPlastic}
                cartSummary={{
                    cartData: cartData,
                    removeItemFromCart: removeItemFromCart,
                    updatePromoCode: updatePromoCode,
                    showPromoCodeInput: false,
                    partnerData: partnerData,
                }}
                showPageLoader={showPageLoader}
            >
                <KYCApplicationForm
                    setHasKycApplication={setHasKycApplication}
                />
            </CheckoutBaseFormat>
        )
    }

    if ((requiresKyc || showKYCForm) && kycIsRejected) {
        return (
            <CheckoutBaseFormat
                currentStep={currentStep}
                hasPlastic={hasPlastic}
                cartSummary={{
                    cartData: cartData,
                    removeItemFromCart: removeItemFromCart,
                    updatePromoCode: updatePromoCode,
                    showPromoCodeInput: false,
                    partnerData: partnerData,
                }}
                showPageLoader={showPageLoader}
            >
                <KYCApplicationDeniedContent
                    numberOfCards={cartData?.resolved_data?.items_count ?? 0}
                    totalLoadValue={Number(
                        cartData?.resolved_data?.load_value_total
                    )}
                    refetchCart={() =>
                        fetchCart({
                            refetchCart: refetchCart,
                            setCartData: setCartData,
                            setRequiresKyc: setRequiresKyc,
                        })
                    }
                />
            </CheckoutBaseFormat>
        )
    }

    if (!shippingDetailsSatisfied || forceShippingStep) {
        return (
            <ShippingStep
                cartData={cartData}
                addDeliveriesToCart={addDeliveriesToCart}
                removeItemFromCart={removeItemFromCart}
                currentStep={currentStep}
                partnerData={partnerData}
            />
        )
    }

    return (
        <CheckoutBaseFormat
            currentStep={currentStep}
            hasPlastic={hasPlastic}
            cartSummary={{
                cartData: cartData,
                removeItemFromCart: removeItemFromCart,
                updatePromoCode: updatePromoCode,
                showPromoCodeInput: true,
                partnerData: partnerData,
            }}
            showPageLoader={showPageLoader}
            backUrl={hasPlastic ? `${backUrlBase}?step=shipping` : homeUrl}
            backText={hasPlastic ? 'Back to Shipping' : 'Back'}
        >
            <CheckoutPaymentProvider
                paymentProcessor={paymentProcessor}
                cartSessionUuid={cartData?.cart_session_uuid}
                orderTotal={cartData?.resolved_data?.order_total}
                virtualLineItemEmails={virtualLineItemEmails}
                hasPGPlusItem={hasPGPlusItems}
                isKYCAndNotRejected={!kycIsRejected && hasKycApplication}
                totalLoadValue={total}
                numberOfCards={numberOfCards}
                cartStateUuid={cartData?.cart_state_uuid}
                setShowLoader={setShowPageLoader}
            >
                <PayPalScriptProvider
                    options={{
                        clientId: paypalClientId,
                        intent: 'authorize',
                        disableFunding: 'paylater',
                        // We can enable Venmo later
                        // enableFunding: 'venmo',
                        integrationDate: '2024-06-13',
                        debug: false,
                    }}
                >
                    <CheckoutForm
                        cartData={cartData}
                        cartSessionUuid={cartData?.cart_session_uuid ?? ''}
                        amazonPayURL={amazonPayURL}
                        isPayPalEnabled={isPayPalEnabled}
                        partnerData={partnerData}
                    />
                </PayPalScriptProvider>
            </CheckoutPaymentProvider>
            {/* eslint-disable-next-line @next/next/no-sync-scripts */}
            <script src="https://www.paypal.com/sdk/js?client-id=AdbYUDEn8dNIFi8YRpvWjHeYWqKROwZE4Fdg5HASu9DxiWI1pSQXb_E3ktgegT_Frnlje3r8Rua2SwLY"></script>
        </CheckoutBaseFormat>
    )
}

export async function getServerSideProps() {
    const paypalClientId = process.env.PAYPAL_CLIENT_ID

    const payPalFeatureFlag = await prisma.featureFlag.findFirst({
        where: {
            name: FEATURE_FLAGS.PAYPAL_BUTTON,
        },
    })
    const isPayPalEnabled: boolean = payPalFeatureFlag?.enabled ?? false

    return {
        props: {
            showHeader: false, //disable standard header in GlobalLayout
            showFooter: false, //disable standard footer in GlobalLayout
            amazonPayURL: getAmazonReturnUrl(),
            paypalClientId,
            isPayPalEnabled,
            content: {
                data: {
                    title: 'Checkout & Purchase',
                    description:
                        'Check out to place your order at BuyGiftCards.com.',
                },
            },
        },
    }
}
